import type { ru } from './ru'

export const en: Record<keyof typeof ru, string> = {
  'lang.self': 'EN',

  'dir.as_map': 'On map',
  'dir.as_list': 'As list',
  'dir.map_none': 'No venues with coordinates',
  'dir.map_failed': 'Map failed to load — please try again',
  'dir.units': 'buildings',
  'dir.pois': 'points',
  'dir.open': 'Open venue',
  'venue.type.industrial_park': 'Industrial park',
  'venue.type.mall': 'Mall',
  'venue.type.airport': 'Airport',
  'venue.type.port': 'Port',
  'venue.type.ship': 'Ship',
  'venue.type.terminal': 'Terminal',
  'venue.type.cruise': 'Cruise ship',
  'dir.title': '{brand} — venue navigator',
  'dir.subtitle': 'Find a venue and get a route to the right spot.',
  'dir.search': 'Search: name, address…',
  'dir.verified': 'Verified organisation',
  'dir.empty': 'Nothing found yet.',
  'map.loading': 'Loading map…',
  'map.error': 'Map unavailable. Check your connection and reload the page.',
  'map.you_are_here': 'You are here',
  'map.attribution': '© Map data · OpenMapTiles · ODbL',
  // Overview header and bottom sheet (Aurora design, 2026-08-05)
  'hdr.live': 'live map',
  'hdr.offline': 'offline',
  'sheet.where_to': 'Where to?',
  'sheet.directory': 'all places',
  'sheet.ai_hints': 'AI hints',
  'target.stat_time': 'time',
  'target.stat_way': 'distance',
  'target.stat_levels': 'levels',
  'target.stat_start': 'start',
  'target.room_n': 'Room {code}',
  'route.arrival_at': 'arriving at',
  'route.remaining': 'left',
  'route.turns_n': '{n} turns',
  'orient.step_one': 'Orientation · step 1',
  'orient.first_step_q': 'Which way is the first step',
  'orient.acc_precise': 'Exact · compass and QR board',
  'orient.acc_approx': 'Approximate · phone sensors',
  'orient.acc_rough': 'Rough · no sensors',
  'orient.north': 'N',
  'orient.east': 'E',
  'orient.south': 'S',
  'orient.west': 'W',
  'follow.arrival': 'arrival',
  'follow.recenter': 'Back to me',
  'follow.stat_done': 'walked',
  'follow.stat_left': 'left',
  'follow.stat_steps': 'steps',

  'cat.warehouse': 'Warehouse',
  'cat.checkpoint': 'Checkpoint',
  'cat.office': 'Dispatch office',
  'cat.dock': 'Gate',
  'cat.truck_scale': 'Weighbridge',
  'cat.wc': 'Restroom',
  'cat.info': 'Info desk',
  'cat.entrance': 'Entrance',
  'cat.food': 'Food',
  'cat.clothing': 'Fashion',
  'cat.sport': 'Sport',
  'cat.tech': 'Electronics',
  'cat.beauty': 'Beauty',
  'cat.grocery': 'Grocery',
  'cat.home': 'Home',
  'cat.service': 'Service',
  'cat.loading_dock': 'Loading dock',
  'cat.parking': 'Parking',
  'cat.elevator': 'Lift',
  'cat.escalator': 'Escalator',
  'cat.stairs': 'Stairs',

  'target.eta': '≈ {time} walk · {dist}',
  'target.route': 'Route',
  'target.no_route': 'No route found — please ask at the checkpoint',
  'target.from_entrance': 'from the main entrance',
  'route.to_floor': 'To floor {n}',
  'target.step_free': 'Step-free route',
  'target.step_free_hint':
    'There is a route to this destination without stairs or steps — by lift and ramps.',

  'travel.label': 'How are you travelling',
  'travel.foot': 'On foot',
  'travel.vehicle': 'By car',
  'travel.truck': 'Truck',

  'route.ext_leg': 'incl. {min} min on streets to the entrance',
  'route.go': "Let's go",

  'floor.title': 'Floors',
  'follow.remaining': '{time} · {dist} left',
  'follow.next_step': 'Next ✓',
  'follow.floor_up_stairs': 'Take the stairs up to floor {n}',
  'follow.floor_down_stairs': 'Take the stairs down to floor {n}',
  'follow.floor_up_escalator': 'Take the escalator up to floor {n}',
  'follow.floor_down_escalator': 'Take the escalator down to floor {n}',
  'follow.floor_up_elevator': 'Take the lift up to floor {n}',
  'follow.floor_down_elevator': 'Take the lift down to floor {n}',
  'follow.floor_up': 'Go up to floor {n}',
  'follow.floor_down': 'Go down to floor {n}',
  'follow.floor_confirm': "I'm on floor {n} ✓",
  'follow.floor_wrong': "I'm still on floor {n}",
  'follow.step_of': 'Step {k} of {n}',
  'follow.arrived': 'You have arrived',
  'follow.arrived_in': 'Made it in {time}',
  'follow.arrive_at': 'arrive ≈ {time}',
  'follow.back_route': 'Way back',
  'follow.stop': 'End',
  'follow.voice_turn': 'In {dist} turn {dir}',
  'follow.voice_left': 'left',
  'follow.voice_right': 'right',
  'follow.voice_arrived': 'You have arrived',
  'follow.alerts_silent': 'Cues: silent',
  'follow.alerts_vibrate': 'Cues: vibration',
  'follow.alerts_voice': 'Cues: voice',
  'follow.exit': 'Exit',
  'follow.details': 'Route details',
  'follow.collapse': 'Collapse',
  'follow.show_panel': 'Show guidance panel',

  'anchor.toast_hint': 'the map already knows where you are',
  'offline.ready': 'works offline',
  'offline.cached': 'offline · map from cache',

  'packs.heading': 'Offline maps',
  'packs.save_venue': '💾 Save venue map · {mb} MB',
  'packs.save_city': '🌍 City offline: {name} · {mb} MB',
  'packs.name_venue': 'Venue map',
  'packs.name_city': 'City: {name}',
  'packs.installed': '✓ Saved · version {version}',
  'packs.downloading': 'Downloading… {percent} %',
  'packs.verifying': 'Checking the file…',
  'packs.warming': 'Fetching fonts and venue data…',
  'packs.keep_open': 'Keep the app on screen — downloading stops in the background',
  'packs.paused': 'Download paused: come back to the app',
  'packs.error': 'Download failed',
  'packs.error_checksum': 'The file arrived damaged',
  'packs.error_storage': 'Not enough space on the device',
  'packs.retry': 'Try again',
  'packs.wifi_hint': 'Better on Wi-Fi: {mb} MB',
  'packs.save_data': 'Data Saver is on — the download may not go through',
  'packs.ios_install_first':
    'First “Add to Home Screen” — otherwise Safari erases the map in 7 days',
  'packs.ios_steps': 'Share → “Add to Home Screen” → open {brand} from the home screen',
  'dock.hint': 'drag to move',

  'qr.dock_label': 'Scan QR',
  'qr.title': 'QR scanner',
  'qr.frame_hint': 'Point the camera at a QR code',
  'qr.permission_denied_title': 'No camera access',
  'qr.permission_denied_body':
    'Allow the camera in your browser settings (the lock icon next to the address) or take a photo of the code.',
  'qr.permission_photo_btn': '📷 Photograph a QR code',
  'qr.unavailable': "This browser can't access the camera — take a photo of the code instead",
  'qr.no_code_found': 'No code found in the photo, try again',
  'qr.scan_again': 'Scan again',
  'qr.result_external_title': 'Link not from {brand}',
  'qr.result_unsafe_title': 'Unsafe link — not opened automatically',
  'qr.result_text_title': 'Recognized text',
  'qr.open_link': 'Open',
  'qr.copy': 'Copy',
  'qr.copied': 'Copied',

  'floor.role_target': 'goal',
  'floor.role_you': 'you are here',
  'floor.role_transit': 'transit',
  'floor.role_parking': 'parking',

  'pos.gps': 'GPS',
  'pos.anchor': 'via QR anchor',
  'pos.none': 'position unknown',

  'common.close': 'Close',
  'common.back': 'Back',
  'common.done': 'Done',

  'view.iso': '3D view',
  'view.map': 'Map',
  'iso.walk': 'Walk the route (preview)',
  'iso.overview': 'Overview',
  'iso.stack': 'Stack',
  'iso.unstack': 'Unstack',
  'iso.reset': 'Reset',
  'iso.hint': 'Drag to orbit · wheel or pinch to zoom',

  'connector.no_alternative':
    'Only one transition leads upstairs on this map — the route is unchanged',
  'connector.row_label': 'How to move between floors',
  'connector.wheelchair': 'Step-free',
  'connector.stroller': 'With a stroller',
  'alarm.title': 'ALARM — follow the red line to the exit',
  'alarm.zones': 'danger zones: {n}',
  'alarm.enable_sound': 'Enable sound',
  'alarm.sound_off': 'Mute',
  'alarm.sound_on': 'Sound on',
  'alarm.blink_off': 'Stop blinking',
  'alarm.blink_on': 'Blink on',
  'connector.escalator': 'Escalator',
  'connector.escalator_note': 'Faster',
  'connector.lift': 'Lift',
  'connector.lift_note': 'Step-free',
  'connector.stairs': 'Stairs',
  'connector.stairs_note': 'Active',

  'car.remember': 'Save where I parked',
  'car.postcard': 'Car spot postcard (PNG with QR)',
  'car.return': 'Back to my car',
  'car.forget': 'Forget car',
  'car.hint':
    'Scan a code on a parking pillar or pick a zone. Automatic floor detection is in the app.',

  'update.ready': 'A new version is available',
  'update.reload': 'Reload',
  'unit.m': 'm',
  'unit.km': 'km',
  'unit.min': 'min',
  'unit.lessMin': '<1 min',

  'cat.gate': 'Gate',
  'cat.check_in': 'Check-in',
  'cat.security': 'Security',
  'cat.baggage': 'Baggage claim',
  'cat.terminal': 'Terminal',
  'cat.berth': 'Berth',
  'cat.deck': 'Deck',
  'cat.cabin': 'Cabin',
  'cat.restaurant': 'Restaurant',
  'cat.muster': 'Muster station',
  'cat.ship': 'Ship',
  'cat.factory': 'Production',
  'cat.shop': 'Shop',
  'cat.building': 'Building',
  'cat.hall': 'Hall',
  'cat.room': 'Room',
  'cat.workspace': 'Workspace',
  'cat.rest_area': 'Seating area',

  'orient.board_straight': 'Face the board — go straight ahead',
  'orient.board_right': 'Face the board — turn right',
  'orient.board_left': 'Face the board — turn left',
  'orient.board_back': 'Put the board behind you — go straight',
  'orient.gate_straight': 'From the board — straight on, we take it from there',
  'orient.gate_right': 'From the board exit right — drive, we take it from there',
  'orient.gate_left': 'From the board exit left — drive, we take it from there',
  'orient.gate_back': 'From the board — turn around, we take it from there',
  'orient.first_leg': '{dist} to the first turn',
  'orient.bind': 'Face the board and pin the arrow',
  'orient.bound': 'Arrow pinned — stays true however you turn the phone',
  'orient.stale': 'The arrow may have drifted — check against the board',
  'orient.walk_hint': 'Walk a few steps — we will refine the direction',
  'orient.go': 'On my way ✓',

  // AR guidance (spec 13): the user picks how to be guided; phone tilt switches
  // modes, an honest badge names the sensor in charge.
  'ar.choice_map': 'Map & line',
  'ar.choice_ar': 'AR guidance',
  'ar.new': 'NEW',
  'ar.course_btn': 'Heading-up',
  'ar.seg_map': 'Map',
  'ar.seg_auto': 'Auto',
  'ar.seg_ar': 'AR',
  'ar.seg_split': 'Camera & map',
  'ar.split_handle': 'Drag the camera/map divider',
  'ar.manual': 'Manual mode — auto will return by itself',
  'ar.auto_back': 'Auto mode re-engaged',
  'ar.no_camera': 'Camera unavailable — guiding on the map',
  'ar.no_direction': 'Refining direction — walk a few steps',
  'ar.sensors_denied': 'Sensors unavailable — the map stays north-up',
  'ar.src_anchor': 'QR anchor',
  'ar.src_course': 'GPS course',
  'ar.src_compass': 'Compass',
  'ar.src_none': 'No direction',
  'ar.src_stale': 'may have drifted',
  'ar.safety_title': 'Safer with the phone lowered',
  'ar.safety_body': 'The map keeps guiding — AR returns when you raise it.',
  'ar.retargeted': 'Guiding to the new target',
  'ar.banner_evac': 'Evacuation — walk, don’t run',
  'ar.cardinal_n': 'N',
  'ar.cardinal_e': 'E',
  'ar.cardinal_s': 'S',
  'ar.cardinal_w': 'W',

  // Spotlight — paid promotion (ADR 0019). The word “Ad” and the advertiser
  // are always visible: hidden advertising is unlawful (RekS §3).
  'promo.ad_label': 'Ad',
  'promo.hide': 'Hide',
  'promo.paid_by': 'Paid for by {name}',
  'promo.by_owner': 'Placed by the venue owner',
  'promo.why_here': 'Why am I seeing this',
  'promo.chip': 'Ad',
  'promo.ranking_title': 'How results are ordered',
  'promo.ranking_intro':
    'We show how the list is built and what in it is paid for. The law requires ' +
    'this of platforms carrying ads — and it is simply fair to you.',
  'promo.ranking_search':
    'Search. Name and product matches come first, by distance from you. A paid ' +
    'row can never outrank an exact match: it comes after every relevant hit ' +
    'and is always marked “Ad”.',
  'promo.ranking_map':
    'Map. Paid emphasis is an outline and a soft glow. It changes neither the ' +
    'name of a place, nor its category, nor where it is.',
  'promo.ranking_limit':
    'Limits. No more than a quarter of a venue’s places can be emphasised: ' +
    'when everything is highlighted, nothing is.',
  'promo.ranking_route':
    'Routing. While you are walking there are no ads at all — the same in ' + 'emergency mode.',
  'promo.ranking_back': 'Back to the map',

  'door.badges': 'About this entrance',
  'door.primary': 'Main entrance',
  'door.ramp': 'Ramp',
  'door.step_free': 'Step-free',
  'door.automatic': 'Automatic door',
  'door.width': 'Opening {cm} cm',

  'search.open': 'Search',
  'search.placeholder': 'Store, product or place…',
  'search.recent': 'Recent',
  'search.usually_here': 'usually available here',
  'search.not_found': 'No results for “{q}”',
  'search.report': 'Tell the venue',
  'search.report_sent': 'Passed to the venue owner — thank you!',

  // ─── Emergency mode (G4, ADR 0009); drill.*/emergency.* — pack strings/emergency ───
  'map.shelter_marker_aria': 'Shelter {label} · {occ}',
  'map.shelter_marker_aria_plain': 'Shelter {label}',
  // ── Вьюер «Terra» (дизайн modern-gis-logistics-application (4)) ──
  'app.tagline': 'Indoor navigation for the last mile',
  'app.tagline_full': '{brand} — Discover. Guide. Arrive.',
  'hdr.tagline': 'Territory navigation',
  'map.empty_hint': 'Tap a building or point on the map',
  'map.view_3d': '3D buildings',
  'map.view_2d': 'Flat plan (2D)',
  'basemap.label': 'Basemap style',
  'basemap.dark_hint': 'Dark basemap',
  'basemap.cad_hint': 'Light CAD basemap',
  'legend.title': 'Territory legend',
  'legend.objects': 'Objects',
  'legend.route': 'Route',
  'legend.route_network': 'Along venue roads',
  'legend.route_direct': 'Off roads — direct line',
  'legend.safety': 'Safety',
  'legend.safety_points': 'Exits and assembly points',
  'mall.error_venue': 'This venue map is unavailable',
  'mall.error_not_mall': 'This venue is not a mall',
  'mall.shops': 'shops',
  'mall.sheet_toggle': 'Expand list',
  'mall.cam_rotate': 'Rotate',
  'mall.cam_tilt': 'Tilt',
  'mall.cam_zoom': 'Zoom',
  'mall.cam_gap': 'Floor gap',
  'mall.view_2d': 'Top',
  'mall.view_3d': '3D',
  'mall.view_2d_hint': 'Floor plans flat, side by side',
  'mall.near_title': 'Nearest',
  'mall.near_wc': 'Restroom',
  'mall.near_lift': 'Lift',
  'mall.near_stairs': 'Escalator',
  'mall.near_food': 'Food',
  'mall.near_exit': 'Exit',
  'mall.mini_open': '{n} places · open →',
  'mall.expand': 'expand',
  'mall.collapse': 'collapse',
  'mall.expand_hint': 'See all floors at once — what is above what',
  'mall.nav_pan': 'Pan',
  'mall.nav_orbit': 'Rotate',
  'mall.hint_camera_3d': 'Left drag — pan · right or Shift+drag — rotate · wheel — zoom',
  'mall.hint_camera_orbit': 'Drag — rotate · wheel — zoom',
  'mall.back_to_map': 'Map',
  'mall.labels_name': 'Names',
  'mall.labels_code': 'Numbers',
  'mall.reset_view': 'Reset view',
  'mall.levels': 'Levels',
  'mall.directory': 'Floor directory',
  'mall.mode': 'MALL MODE',
  'mall.all_levels': 'All levels',
  'mall.floor_stats': '{n} places on this floor',
  'mall.search_placeholder': 'Find a store on this floor…',
  'mall.empty': 'This floor is empty',
  'a2b.kicker': 'Territory Wayfinding',
  'a2b.title': 'Route A → B',
  'a2b.from': 'FROM (POINT A)',
  'a2b.to': 'TO (POINT B)',
  'a2b.pick': 'Pick a destination…',
  'a2b.distance': 'DISTANCE',
  'a2b.time': 'TRAVEL TIME',
  'a2b.build': 'Draw on the map',
  'measure.kicker': 'GIS measuring tape',
  'measure.title': 'Measure distances and areas',
  'measure.points': '{n} points',
  'measure.hint': 'Click the map to drop measuring points',
  'measure.clear': 'Clear',
  'measure.done': 'Done',
  'qrpass.kicker_venue': 'Venue QR',
  'qrpass.venue_open': 'Venue QR code',
  'qrpass.kicker': 'QR navigation to target',
  'qrpass.alt': 'QR code linking to the target',
  'qrpass.scan_hint': 'Scan with a phone camera — the map opens with this target',
  'qrpass.copy': 'Link',
  'qrpass.copied': 'Copied',
  'qrpass.share': 'Share',
  // Схема пути из таблички (&r=): показывается, когда карта площадки не пришла.
  'sign.route_title': 'Exit route from the sign',
  'sign.route_hint': 'The venue map did not load — showing the route stored in the QR code',
  'sign.route_start': 'You are here',
  'sign.route_exit': 'Exit',
  'unit.m2': 'm²',

  // Языковое меню (самоназвания — одинаковы во всех словарях)
  'lang.name_ru': 'Русский',
  'lang.name_en': 'English',
  'lang.name_et': 'Eesti',

  // Basemap style menu (2026-07-26)
  'basemap.title': 'Map style',
  'basemap.style_dark': 'Dark',
  'basemap.style_cad': 'Blueprint',
  'basemap.style_liberty': 'Classic',
  'basemap.style_bright': 'Bright',
  'basemap.style_fiord': 'Fiord',
  'basemap.style_saved': 'Saved map',
  'floor.all': 'All floors',

  // Search palette (reference (7), 2026-07-26)
  'search.tab_all': 'All',
  'search.tab_tenants': 'Tenants',
  'search.tab_buildings': 'Buildings',
  'search.tab_infra': 'Infrastructure',
  'search.open_now': 'Open now',
  'search.quick_title': 'Quick searches',
  'search.quick_food': 'Where to eat',
  'search.quick_warehouse': 'Warehouses & dispatch',
  'search.quick_dock': 'Truck docks',
  'search.quick_entrance': 'Building entrances',
  'search.quick_parking': 'Parking',
  'search.quick_checkpoint': 'Checkpoints',
  'search.quick_milk': 'Milk',
  'search.quick_bread': 'Bread',
  'search.quick_checkout': 'Checkout',
  'search.quick_wc': 'Toilet',
  'search.tab_products': 'Products',

  'shopping.kicker': 'My list',
  'shopping.title': 'Shopping list',
  'shopping.empty': 'The list is empty. Find a product in search and tap “+”.',
  'shopping.add': 'Add to list',
  'shopping.remove': 'Remove from list',
  'shopping.clear': 'Clear list',
  'shopping.tour_btn': 'Visit all ({n})',
  'shopping.tour_hide': 'Hide route',
  'shopping.stops_n': '{n} stops',
  'shopping.share': 'Share list',
  'shopping.share_copied': 'Link copied',
  'shopping.promo_title': 'On offer now',
  'target.add_to_list': 'Add to shopping list',
  'target.in_list': 'In the list',
  'target.usually_here': 'Usually stocked here',
  'search.open_badge': 'Open',
  'search.closed_badge': 'Closed',
  'search.floor_n': 'fl. {n}',
  'search.route_btn': 'Build route',
  'search.hint_open': 'open',
  'search.hint_route': 'route',
  'search.found_n': 'Found: {n}',
  'search.smart_hint':
    'Search understands typos, Latin letters and a wrong keyboard layout. Try “cafe”, “warehouse” or “dock”.',

  // Detailed card (2026-07-27)
  'card.floor_n': 'floor {n}',
  'card.floors_n': 'Floors: {n}',
  'card.tenants_n': 'Tenants: {n}',
  'card.tenants_title': 'Tenants',
  'card.how_to_find': 'How to find inside',
  'card.edit_link': 'Edit in cabinet',

  'search.all_objects': 'All objects',
  'catalog.title': 'Catalog',
  'catalog.tenants': 'Companies',
  'catalog.buildings': 'Buildings',
  'catalog.hover_hint': 'Hover a row — the object lights up on the map',
  'catalog.other': 'Other',
  'catalog.filter_placeholder': 'Filter: name or category…',
  'card.photo_n': 'Photo {n}',
  'card.more': 'More details',
  'card.less': 'Hide details',
  'floorstack.title': 'Building floors',
  'volume3d.kpi_floor': 'Floor',
  'volume3d.kpi_area': 'Area',
  'volume3d.interior': 'Interior',
  'volume3d.outside': 'Outside',
  'volume3d.route_here': 'Route drawn inside the building',
  'volume3d.tenants_on_floor': 'Tenants on this floor',
  'volume3d.kpi_cabin': 'Cabin',
  'cabin.interior': 'Interior',
  'cabin.oceanview': 'Oceanview',
  'cabin.balcony': 'Balcony',
  'cabin.suite': 'Suite',
  'cabin.port': 'Port side',
  'cabin.starboard': 'Starboard side',
  'volume3d.search': 'Search tenant or cabin',
  'volume3d.kpi_guests': 'Guests',
  'volume3d.route_len': 'Walk {m} m on this floor',
  'volume3d.route_none': 'No way through on this floor — check the route on the map',
  'volume3d.all': 'All',
  'volume3d.search_short': 'Search',
  'volume3d.search_empty': 'Nothing found — try another word',
  'volume3d.search_more': 'Showing {shown} of {total}',
  'volume3d.search_count': 'Found: {n}',
  'mall.all_short': 'All floors',
  'volume.choose': 'Choose volume view',
  'volume.kind_iso': 'Volume',
  'volume.kind_iso_hint': 'Whole venue, floors side by side',
  'volume.kind_stack': 'Floor stack',
  'volume.kind_stack_hint': 'Building floors stacked on the map',
  'volume.kind_three': 'Volume 3D',
  'volume.kind_three_hint': 'Floor from inside, with tenants',
  'volume3d.open': '3D volume',
  'volume3d.title': 'Building in 3D',
  'volume3d.floor_n': 'Floor {n}',
  'volume3d.explode': 'Spread floors',
  'volume3d.reset': 'Reset view',
  'volume3d.downgraded': 'Simplified the picture to keep it smooth on this device.',
  'volume3d.failed': '3D view could not start on this device — the flat “Volume” is available.',
  'floorstack.open': 'Volume · {n} fl.',
  'floorstack.vacant': 'No tenants on floor {n}',
  'floorstack.hint': 'Tap a floor slab or chip to select a floor',
  // ── hazards: точечные опасности, городская тревога (24.09.2026); staff.* — пакет strings/staff ──
  'route.detour': 'Detour: {kind} · +{m} m',
  'route.detour_floor': 'Detour: {kind}, floor {level} · +{m} m',
  'route.detour_no_m': 'Detour: {kind}',
  'route.detour_floor_no_m': 'Detour: {kind}, floor {level}',
  'route.detour_zone': 'hazard zone',
  'route.unreachable': 'Cannot reach target — {kind}',
  'alarm.city_label': 'City',
  'alarm.drill_tag': 'DRILL · ÕPPUS',
  'alarm.venue.fire': 'Fire — follow the exit route on the map',
  'alarm.venue.evac': 'Evacuation — follow the exit route on the map',
  'alarm.venue.flood': 'Flooding — go to upper floors along the route on the map',
  'alarm.venue.danger': 'Danger — follow staff instructions',
  'alarm.venue.shelter': 'Stay inside the building — move away from windows',
  'alarm.city.air': 'Air raid alert — go to a shelter',
  'alarm.city.evac': 'Area evacuation — follow instructions from the authorities',
  'alarm.levels': 'Alert: floor {name}',
  'alarm.checked_ago': 'Checked {min} min ago',
  'alarm.checked_now': 'Checked just now',
  'alarm.offline': 'No connection — showing last known signal',
  'alarm.self_title': 'You turned this on yourself — no official signal',
  'alarm.relay_title': 'Alarm passed on from another phone — not verified',
  // ── sos: всегда видимые аварийные кнопки, офлайн-статус, публичные укрытия (24.09.2026) ──
  'sos.button': 'SOS',
  'sos.sheet_title': 'What do you need?',
  'sos.shelter': 'Shelter',
  'sos.exit': 'Exit',
  'sos.call112': '112',
  'sos.disclaimer': "These buttons don't notify anyone — they only guide you.",
  'sos.status_offline': 'No connection — map works offline',
  'sos.status_checked': 'No official signal · checked {n} min ago',
  'sos.status_unknown': 'No official signal',
  'shelters.geo_asking': 'Finding your location to show the nearest shelter',
  'shelters.geo_denied': 'No location access — list from the city centre',
  'shelters.straight_line': 'straight line — the road may be longer',
  'shelters.route_btn': 'Route',
  'shelters.none': 'No shelters found nearby',
  'cityalert.air': 'Air raid alert',
  'cityalert.evac': 'Evacuation',
  'cityalert.drill_badge': 'DRILL · ÕPPUS',
  'cityalert.finding_you': 'Finding your location to show the way to the nearest shelter',
  // ── hazard-map: обход опасностей обычным маршрутом, зона опасности на карте (24.09.2026) ──
  'hazard.kind.fire': 'Fire',
  'hazard.kind.smoke': 'Smoke',
  'hazard.kind.flood': 'Flooding',
  'hazard.kind.danger': 'Danger',
  'hazard.kind.closed': 'Passage closed',
} as const
